import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { Responsable, ResponsablePagingRes } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { getUrlParamsFetch, handleAxiosError } from '@/shared';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchResponsable = (params?: GetResponsableParams) => {
  return useQuery({
    queryKey: ['adminResponsable', ...Object.values(params || {})],
    queryFn: async () => {
      return getResponsables(params);
    },
  });
};

export const useGetResponsable = (id: string) => {
  return useQuery({
    queryKey: ['adminResponsable', id],
    queryFn: async () => {
      return getResponsable(id);
    },
  });
};

export const useCreateResponsable = (
  navigate: NavigateFunction,
  returnUrl: string,
  enableNavigation?: boolean
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createResponsable,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminResponsable'] });

      enableNavigation && navigate(returnUrl, { replace: true });
    },
    onError: error => {
      handleAxiosError(error);
    },
  });
};

export const useUpdateResponsable = (
  navigate: NavigateFunction,
  returnUrl: string,
  noState?: boolean,
  enableNavigation?: boolean
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateResponsable,
    onSuccess: () => {
      noState &&  queryClient.invalidateQueries({ queryKey: ['adminResponsable'] });
      enableNavigation && navigate(returnUrl, { replace: true });
    },
    onError: error => {
      handleAxiosError(error);
    },
  });
};

///* axios
export type GetResponsableParams = {
  page?: number;
  page_size?: number;
  nombre?: string;
  codigo?: string;
};
export type CreateResponsableParams = Omit<
  Responsable,
  'id_responsable'
>;
export type UpdateResponsableParams = {
  id: string;
  data: CreateResponsableParams;
};

export const getResponsables= async (params?: GetResponsableParams) => {
  const queryParams = getUrlParamsFetch({ ...params });

  return get<ResponsablePagingRes>(
    `${urlBackend}api/responsable/?${queryParams}`,
    true
  );
};

export const getResponsable = async (id: string) => {
  try{
    return await get<Responsable>(
    `${urlBackend}api/responsable/${id}`,
    true
  );
  }
  catch(error){
    return handleAxiosError(error);
  }
};

export const createResponsable = async (data: CreateResponsableParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<Responsable>(
    `${urlBackend}api/v1/responsable/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const updateResponsable = async ({
  id,
  data,
}: UpdateResponsableParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<Responsable>(
    `${urlBackend}api/v1/responsable/${id}/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};
